import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  Link2,
  Copy,
  MessageCircle,
  Loader2,
  CheckCircle,
  Sparkles,
  IndianRupee,
  Percent,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../../atoms/Utlis/Toast";
import {
  createPaymentLinkAPI,
  sharePaymentLinkWhatsAppAPI,
} from "../../../../../Redux/ApiCalls/Dashboard/InvoiceAPI";
import type { InvoiceInstallment } from "../../../../../Data/invoice.dto";
import { getHostUrl } from "../../../../../Redux/ApiCalls/getHostUrl";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
} from "../../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../../quotationMasterListShared";
import {
  Field,
  Label,
  FormLabelIcon,
} from "../../../../molecules/Dashboard/Member/memberFormStyles";

interface PaymentLinkModalProps {
  invoiceId: number;
  installment: InvoiceInstallment;
  onClose: () => void;
  onSuccess: () => void;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 0;
  gap: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
`;

const InstallmentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.1) 0%, rgba(206, 86, 176, 0.08) 100%);
  border: 1px solid rgba(201, 77, 170, 0.14);
`;

const InstallmentIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.12);
`;

const InstallmentBody = styled.div`
  min-width: 0;
`;

const InstallmentLabel = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const InstallmentMeta = styled.div`
  margin-top: 3px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(49, 166, 106, 0.08);
  border: 1px solid rgba(49, 166, 106, 0.18);
`;

const SuccessIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(49, 166, 106, 0.14);
  color: #31a66a;
`;

const SuccessText = styled.div`
  min-width: 0;
`;

const SuccessTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const SuccessSub = styled.div`
  margin-top: 2px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const LinkBox = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fafafa;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  word-break: break-all;
  line-height: 1.5;
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ActionBtn = styled.button<{ $tone?: string; $solid?: boolean }>`
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$solid ? "transparent" : "rgba(23, 23, 23, 0.12)")};
  background: ${(p) => (p.$solid ? (p.$tone || "#25d366") : "#fff")};
  color: ${(p) => (p.$solid ? "#fff" : "rgba(23, 23, 23, 0.72)")};
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$solid ? `0 6px 16px ${p.$tone || "#25d366"}40` : "none")};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: ${(p) => (p.$solid ? "transparent" : "rgba(201, 77, 170, 0.25)")};
    color: ${(p) => (p.$solid ? "#fff" : "#c94daa")};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const PaymentLinkModal: React.FC<PaymentLinkModalProps> = ({
  invoiceId,
  installment,
  onClose,
  onSuccess,
}) => {
  const [linkUrl, setLinkUrl] = useState<string | null>(installment.payment_link_url || null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerateLink = async () => {
    try {
      setGeneratingLink(true);
      const hostName = getHostUrl();
      const result = await createPaymentLinkAPI(invoiceId, installment.id!, {
        host_name: hostName,
      });
      setLinkUrl(result.link_url);
      showSuccessToast("Payment link created ✅");
      onSuccess();
    } catch (err: any) {
      const responseData = err?.response?.data;
      const errorMsg =
        responseData?.message ||
        responseData?.error?.message ||
        err?.message ||
        "Failed to create payment link.";
      showErrorToast(errorMsg);
    } finally {
      setGeneratingLink(false);
    }
  };

  useEffect(() => {
    if (!linkUrl) {
      handleGenerateLink();
    }
  }, []);

  const handleCopy = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showSuccessToast("Link copied to clipboard");
    } catch {
      showErrorToast("Could not copy to clipboard");
    }
  };

  const handleShareWhatsApp = async () => {
    if (!installment.id) return;
    try {
      setSharing(true);
      await sharePaymentLinkWhatsAppAPI(invoiceId, installment.id);
      showSuccessToast("Payment link shared on WhatsApp 🎉");
      onSuccess();
    } catch (err: any) {
      const responseData = err?.response?.data;
      const errorMsg =
        responseData?.message ||
        responseData?.error?.message ||
        err?.message ||
        "Failed to share payment link.";
      showErrorToast(errorMsg);
    } finally {
      setSharing(false);
    }
  };

  const heroTitle = generatingLink && !linkUrl
    ? "Generating Payment Link"
    : linkUrl
      ? "Payment Link Ready"
      : "Get Payment Link";

  const heroSubtitle = generatingLink && !linkUrl
    ? "Creating a secure Cashfree payment link for this installment."
    : linkUrl
      ? "Copy the link or send it to your customer on WhatsApp."
      : "Generate a shareable payment link for this installment.";

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalShell style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                {linkUrl ? "Link ready" : "Invoice payment"}
              </HeroBadge>
              <ModalTitle>{heroTitle}</ModalTitle>
              <ModalSubtitle>{heroSubtitle}</ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody>
          <FormCard style={{ gap: 16 }}>
            <InstallmentCard>
              <InstallmentIcon>
                <Percent size={20} />
              </InstallmentIcon>
              <InstallmentBody>
                <InstallmentLabel>{installment.label}</InstallmentLabel>
                <InstallmentMeta>
                  {installment.percent}% share
                  {installment.amount ? ` · ₹${installment.amount}` : ""}
                </InstallmentMeta>
              </InstallmentBody>
              {installment.amount && (
                <InstallmentIcon style={{ marginLeft: "auto" }}>
                  <IndianRupee size={20} />
                </InstallmentIcon>
              )}
            </InstallmentCard>

            {generatingLink && !linkUrl ? (
              <LoadingWrap>
                <Spinner size={28} color="#c94daa" />
                Generating payment link...
              </LoadingWrap>
            ) : !linkUrl ? (
              <PrimaryButton type="button" onClick={handleGenerateLink} disabled={generatingLink}>
                {generatingLink ? <Spinner size={16} /> : <Link2 size={16} />}
                {generatingLink ? "Generating..." : "Generate Payment Link"}
              </PrimaryButton>
            ) : (
              <>
                <SuccessBanner>
                  <SuccessIconWrap>
                    <CheckCircle size={22} />
                  </SuccessIconWrap>
                  <SuccessText>
                    <SuccessTitle>Payment link generated</SuccessTitle>
                    <SuccessSub>
                      {installment.label} · {installment.percent}%
                      {installment.amount ? ` · ₹${installment.amount}` : ""}
                    </SuccessSub>
                  </SuccessText>
                </SuccessBanner>

                <Field>
                  <Label>
                    <FormLabelIcon icon={Link2} />
                    Payment Link
                  </Label>
                  <LinkBox>{linkUrl}</LinkBox>
                </Field>

                <ActionGrid>
                  <ActionBtn type="button" onClick={handleCopy}>
                    {copied ? <CheckCircle size={15} /> : <Copy size={15} />}
                    {copied ? "Copied" : "Copy Link"}
                  </ActionBtn>
                  <ActionBtn
                    type="button"
                    $tone="#25d366"
                    $solid
                    onClick={handleShareWhatsApp}
                    disabled={sharing}
                  >
                    {sharing ? <Spinner size={15} /> : <MessageCircle size={15} />}
                    {sharing ? "Sending..." : "Send on WhatsApp"}
                  </ActionBtn>
                </ActionGrid>
              </>
            )}
          </FormCard>
        </ModalBody>

        <ModalActions>
          <SecondaryButton type="button" onClick={onClose}>
            Close
          </SecondaryButton>
          {linkUrl && (
            <PrimaryButton type="button" onClick={onClose}>
              Done
            </PrimaryButton>
          )}
        </ModalActions>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

export default PaymentLinkModal;
